import {
  Fragment,
  isValidElement,
  type ReactNode,
  type RefObject,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import { cn } from '@dinewise/utils';
import {
  Dialog,
  DialogPanel,
  DialogTitle,
  Portal,
  Transition,
  TransitionChild,
} from '@headlessui/react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { ArrowLeftIcon } from '@heroicons/react/24/solid';
import { AnimatePresence, motion } from 'motion/react';
import { useLockBackgroundScroll } from './hooks/use-lock-background-scroll.ts';
import { useModalLayerZClassName } from './modal-layer.tsx';

export type ModalOverlayRef = {
  open: () => void;
  close: () => void;
};

type ModalOverlayProps = {
  visible?: boolean;
  onClose?: () => void;
  icon?: ReactNode;
  pretitle?: ReactNode;
  title?: ReactNode;
  postTitle?: ReactNode;
  /** Full-width slot below the title row, inside the sticky header (e.g. step tabs). */
  subHeader?: ReactNode;
  body?: ReactNode;
  bodyRef?: RefObject<HTMLDivElement | null>;
  footer?: ReactNode;
  position?: 'center' | 'top';
  classNames?: {
    body?: string;
    footer?: string;
    panel?: string;
    preTitle?: string;
    title?: string;
    titleText?: string;
    postTitle?: string;
    content?: string;
    panelContainer?: string;
    titleContainer?: string;
    titleRow?: string;
    closeButton?: string;
    closeIcon?: string;
  };
  showCloseButton?: boolean;
  closeLabel?: string;
  showBackButton?: boolean;
  unmount?: boolean;
  disableDismiss?: boolean;
  disableDismissOutside?: boolean;
  slideInFromBottom?: boolean;
  hideOnBackdropClick?: true;
  nonBlocking?: boolean;
} & { modalRef?: RefObject<ModalOverlayRef | null> };

const onDialogClose = () => {};

export const ModalOverlay = ({
  visible: initialVisible,
  unmount,
  onClose,
  pretitle,
  title,
  postTitle,
  subHeader,
  icon,
  body,
  bodyRef,
  footer,
  showCloseButton = true,
  closeLabel = 'Close',
  classNames,
  disableDismiss = false,
  disableDismissOutside = false,
  position = 'center',
  modalRef: ref,
  showBackButton = false,
  slideInFromBottom = false,
}: ModalOverlayProps) => {
  const [visible, setVisible] = useState(initialVisible ?? false);
  const layerZClassName = useModalLayerZClassName();
  useLockBackgroundScroll(visible);

  const onDismissButtonClick = useCallback(() => {
    if (disableDismiss) {
      return;
    }
    setVisible(false);
    onClose?.();
  }, [onClose, disableDismiss]);

  const handleClose = useCallback(() => {
    if (disableDismiss || disableDismissOutside) {
      return;
    }

    setVisible(false);
    onClose?.();
  }, [onClose, disableDismiss, disableDismissOutside]);

  // Keep the visible state in sync with the initialVisible prop
  useEffect(() => {
    if (initialVisible !== undefined) {
      setVisible(initialVisible);
    }
  }, [initialVisible]);

  // Headless UI routes Escape and outside clicks through the same onClose, so modals opting out of
  // outside dismissal would otherwise lose Escape too.
  useEffect(() => {
    if (!visible || disableDismiss || !disableDismissOutside) {
      return;
    }

    const handleEscapeKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') {
        return;
      }

      event.preventDefault();
      setVisible(false);
      onClose?.();
    };

    document.addEventListener('keydown', handleEscapeKeyDown);

    return () => {
      document.removeEventListener('keydown', handleEscapeKeyDown);
    };
  }, [visible, disableDismiss, disableDismissOutside, onClose]);

  useImperativeHandle(ref, () => ({
    open: () => {
      setVisible(true);
    },
    close: () => {
      setVisible(false);
    },
  }));

  const closeButton = showCloseButton ? (
    <button
      type="button"
      aria-label={closeLabel}
      onClick={onDismissButtonClick}
      className={cn(
        'ml-2 flex shrink-0 cursor-pointer items-center justify-center border-0 bg-transparent p-0',
        classNames?.closeButton,
      )}
    >
      <XMarkIcon
        className={cn('size-8 text-ande-black hover:text-gray-500', classNames?.closeIcon)}
      />
    </button>
  ) : null;

  return (
    <AnimatePresence initial={false}>
      {visible ? (
        <Dialog
          {...{ unmount }}
          open={visible}
          onClose={handleClose}
          className={cn('relative', layerZClassName)}
        >
          <motion.div
            aria-hidden="true"
            className="fixed inset-0 bg-[#131414]/45 backdrop-blur-[2px]"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2, ease: 'easeOut' }}
          />
          <div
            data-scroll-lock-allow=""
            className={cn(
              'fixed inset-0 w-screen overflow-y-auto overscroll-contain',
              classNames?.panelContainer,
            )}
          >
            <div
              className={cn(
                'flex min-h-full justify-center p-0 sm:px-6',
                position === 'top' ? 'sm:items-start sm:py-10' : 'items-center',
              )}
            >
              <motion.div
                initial={
                  slideInFromBottom
                    ? { opacity: 0, y: 36, scale: 1 }
                    : { opacity: 0, y: 12, scale: 0.99 }
                }
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={
                  slideInFromBottom ? { opacity: 0, y: 36 } : { opacity: 0, y: 12, scale: 0.99 }
                }
                transition={{ duration: 0.24, ease: 'easeOut' }}
              >
                <DialogPanel
                  className={cn(
                    'max-h-full min-h-screen w-screen max-w-full overflow-hidden rounded-none bg-white text-left align-middle shadow-2xl ring-1 ring-black/5 sm:min-h-0 sm:rounded-2xl sm:border sm:border-ande-gray-100 md:my-8 md:max-h-[90vh] md:max-w-2xl',
                    classNames?.panel,
                  )}
                >
                  <div
                    data-scroll-lock-allow=""
                    className={cn(
                      'overflow-y-auto overscroll-contain max-sm:h-full md:max-h-[90vh]',
                      classNames?.panel,
                    )}
                  >
                    <div
                      className={cn(
                        'flex min-h-screen w-full flex-col divide-y divide-gray-200 max-sm:h-full md:min-h-0',
                        classNames?.content,
                      )}
                    >
                      {/* title */}
                      {title ? (
                        <div
                          className={cn(
                            'sticky top-0 z-10 max-sm:flex-initial',
                            subHeader
                              ? 'flex shrink-0 flex-col border-b border-ande-gray-100 bg-white px-6 pt-6'
                              : 'flex max-h-32 flex-row items-center justify-between border-ande-gray-200/80 bg-ande-white/95 px-8 py-6 backdrop-blur-sm max-sm:border-b md:flex-1',
                            classNames?.title,
                          )}
                        >
                          <div
                            className={cn(
                              'flex w-full flex-row items-center justify-between',
                              subHeader ? 'gap-4' : null,
                              classNames?.titleRow,
                            )}
                          >
                            <div
                              className={cn(
                                'flex flex-row items-center',
                                subHeader ? 'min-w-0' : 'w-full',
                              )}
                            >
                              {icon ? <span className="mr-2">{icon}</span> : null}
                              <div
                                className={cn(
                                  'flex flex-col justify-between gap-1',
                                  classNames?.titleContainer,
                                )}
                              >
                                {pretitle ? (
                                  <div className="flex items-center gap-2">
                                    <div
                                      className={cn('text-gray-400 text-xs', classNames?.preTitle)}
                                    >
                                      {pretitle}
                                    </div>
                                  </div>
                                ) : null}

                                <div className="flex gap-2 items-center">
                                  {showBackButton ? (
                                    <ArrowLeftIcon
                                      className="size-7 shrink-0 cursor-pointer text-ande-black"
                                      onClick={onDismissButtonClick}
                                    />
                                  ) : null}
                                  <DialogTitle
                                    as={isValidElement(title) ? 'div' : 'h1'}
                                    className={cn(
                                      'line-clamp-2 font-normal font-serif text-heading2',
                                      classNames?.titleText,
                                    )}
                                  >
                                    {title}
                                  </DialogTitle>
                                </div>
                                {postTitle ? (
                                  <div
                                    className={cn(
                                      'mt-2 text-ande-gray-300 text-xs',
                                      classNames?.postTitle,
                                    )}
                                  >
                                    {postTitle}
                                  </div>
                                ) : null}
                              </div>
                            </div>
                            {closeButton}
                          </div>
                          {subHeader}
                        </div>
                      ) : null}
                      {/* body */}
                      {body ? (
                        <div
                          ref={bodyRef}
                          className={
                            classNames?.body ??
                            'flex flex-2 flex-grow flex-col items-start gap-6 overflow-y-auto px-8 py-6 max-sm:flex-1'
                          }
                        >
                          {body}
                        </div>
                      ) : null}
                      {/* footer */}
                      {footer ? (
                        <div
                          className={cn(
                            'sticky bottom-0 flex flex-row items-center gap-4 border-gray-300 bg-white px-8 py-6 max-sm:bottom-0 max-sm:w-full max-sm:flex-initial max-sm:border-t md:flex-1',
                            classNames?.footer,
                          )}
                        >
                          {footer}
                        </div>
                      ) : null}
                    </div>
                  </div>
                </DialogPanel>
              </motion.div>
            </div>
          </div>
        </Dialog>
      ) : null}
    </AnimatePresence>
  );
};

export const SidebarOverlay = ({
  visible,
  unmount,
  onClose,
  title,
  icon,
  body,
  bodyRef,
  footer,
  classNames,
  hideOnBackdropClick,
  nonBlocking = false,
}: ModalOverlayProps) => {
  const completeButtonRef = useRef(null);
  const nonBlockingPanelRef = useRef<HTMLDivElement>(null);
  const layerZClassName = useModalLayerZClassName();

  const getPanelContent = (className?: string, panelRef?: RefObject<HTMLDivElement | null>) => (
    <div
      ref={panelRef}
      className={cn(
        'fixed top-0 right-0 h-full max-h-full w-full max-w-full overflow-y-auto bg-white md:max-w-xl',
        className,
        classNames?.panel,
      )}
    >
      <div className="flex h-full w-full flex-col">
        <div
          className={cn(
            'flex flex-shrink-0 flex-row items-center justify-between gap-3 border-gray-300 border-b px-4 py-6',
            classNames?.titleRow,
          )}
        >
          <div className={cn('flex min-w-0 flex-1 items-center', classNames?.titleContainer)}>
            {icon ? <span className="mr-2 shrink-0">{icon}</span> : null}
            <h1 className={cn('min-w-0 w-full font-medium text-2xl text-black', classNames?.title)}>
              {title}
            </h1>
          </div>
          <XMarkIcon
            ref={completeButtonRef}
            className={cn(
              'size-10 shrink-0 cursor-pointer text-gray-500 hover:text-gray-800',
              classNames?.closeIcon,
            )}
            onClick={onClose}
          />
        </div>
        <div
          ref={bodyRef}
          className={cn(
            classNames?.body ??
              'force-scrollbar flex flex-1 flex-row items-start justify-between overflow-y-scroll px-4 py-2 md:overflow-scroll',
          )}
        >
          {body}
        </div>

        {footer ? (
          <div
            className={cn(
              'flex flex-shrink-0 flex-row items-center justify-between border-gray-300 border-t px-8 py-6',
              classNames?.footer,
            )}
          >
            {footer}
          </div>
        ) : null}
      </div>
    </div>
  );

  if (nonBlocking) {
    return (
      <Portal.Group target={nonBlockingPanelRef}>
        <Transition {...{ unmount }} show={visible} as={Fragment}>
          <TransitionChild
            unmount={unmount}
            as={Fragment}
            enter="transition ease-in-out duration-300 transform"
            enterFrom="translate-x-full h-full"
            enterTo="translate-x-0 h-full"
            leave="transition ease-in-out duration-300 transform"
            leaveFrom="translate-x-0 h-full"
            leaveTo="translate-x-full h-full"
          >
            {getPanelContent(layerZClassName, nonBlockingPanelRef)}
          </TransitionChild>
        </Transition>
      </Portal.Group>
    );
  }

  // The layer lives on the Dialog root, not the panel: Headless UI appends nested portals to the
  // root, so anchored menus inside the sidebar would otherwise be siblings of the panel and lose
  // to its z-index.
  return (
    <Transition {...{ unmount }} show={visible} as={Fragment}>
      <Dialog
        {...{ unmount }}
        onClose={onDialogClose}
        initialFocus={completeButtonRef}
        className={cn('relative', layerZClassName)}
      >
        <DialogPanel
          className={cn(
            'fixed top-0 left-0 size-full items-center justify-center bg-black bg-opacity-25',
            hideOnBackdropClick ? 'backdrop-blur-sm' : '',
          )}
          onClick={event => {
            if (hideOnBackdropClick && event.target === event.currentTarget) {
              onClose?.();
            }
          }}
        >
          <TransitionChild
            unmount={unmount}
            as={Fragment}
            enter="transition ease-in-out duration-300 transform"
            enterFrom="translate-x-full h-full"
            enterTo="translate-x-0 h-full"
            leave="transition ease-in-out duration-300 transform"
            leaveFrom="translate-x-0 h-full"
            leaveTo="translate-x-full h-full"
          >
            {getPanelContent()}
          </TransitionChild>
        </DialogPanel>
      </Dialog>
    </Transition>
  );
};
